import { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppMenuSection, isRouteActive } from "../../../../../../../../common/routesUtils";
import { useBreadcrumbs } from "../../../../../../../../context/BreadcrumbsProvider";
import { usePermissions } from "../../../../../../../../permissions/usePermissions";
import SectionItem from "../SectionItem";

interface Props {
	menuIsOpen: boolean;
	item: AppMenuSection;
}

/**
 * SingleRoute renders a single item of the application side menu which
 * contains only 1 route.
 *
 * @param menuIsOpen this flag is used to correctly render the menu item
 * @param item       section and routes managed by the item
 * @returns          an item of the application side menu
 */
const SingleRoute: FC<Props> = ({ menuIsOpen, item }) => {
	const navigateTo = useNavigate();
	const { breadcrumbs, visitPage } = useBreadcrumbs();
	const { pathname } = useLocation();
	const { canAccessPage } = usePermissions();

	// Determine if this menu item is selected based on the current route (nested pages included) or the last breadcrumb
	const isSelected =
		(item.route !== undefined && isRouteActive(item.route, pathname)) ||
		(breadcrumbs.length > 0 && breadcrumbs[breadcrumbs.length - 1].pageType === item.pageType);

	const itemClicked = () => {
		if (item.route) {
			visitPage(item.pageType, true); // mark breadcrumb and reset
			navigateTo(item.route); // navigate to route
		}
	};

	return (
		<SectionItem
			itemKey={item.key}
			disabled={!canAccessPage(item.pageType)}
			state={isSelected ? "active" : undefined}
			menuIsOpen={menuIsOpen}
			onClick={itemClicked}
		/>
	);
};

export default SingleRoute;
